import {
  Table,
  Flex,
  SearchIcon,
  Input,
  ProviderConsumer as FluentUIThemeConsumer,
  SiteVariablesPrepared,
  Checkbox,
} from '@fluentui/react-northstar';
import { gridNestedBehavior } from '@fluentui/accessibility';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toTitleCase } from '../../utils/helper';
import Styles from './table.module.css';
import { useHistory, useLocation } from 'react-router-dom';
import { KBTableTheme } from '../../../kb/KBTableTheme';
import { TeamsTheme } from '@fluentui/react-teams/lib/esm/themes';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { TICKET_PAGINATION, USER_LICENSE_HEADERS } from '../../utils/constants';
import { useTranslation } from 'react-i18next';
import { FontIcon } from '@fluentui/react';
import { ResultCount } from '../ResultCount';
import { TableAction } from './TableAction';
import { usePersistedTableSort } from '../../hooks/usePersistedTableSort';
import { buildRowsDataCompare, TableSortConfig } from './tableSortConfig';
import { buildSortableTableHeader } from './TableSortHeaderMenu';

export const getHeaders = (
  headers: string[],
  headerNotTitleCase: boolean
): HeaderList => {
  const data: HeaderList = {
    key: 'header',
    items: [],
  };
  headers.forEach((header: string) => {
    data.items.push({
      content: headerNotTitleCase ? header : toTitleCase(header),
      key: header.toLowerCase(),
    });
  });

  return { ...data, className: `${Styles.tableHeader}` };
};

interface Props {
  headers: string[];
  rows: RowsData[];
  routeName: string;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  onRowClickHandler?: (id: number) => void;
  headerNotTitleCase?: boolean;
  hideSearch?: boolean;
  searchText?: string;
  onSearchKeyChanged?: (data: string) => void;
  archiveViewSwitch?: boolean;
  onArchiveSwitchChange?: (IsArchived: boolean) => void;
  ifEmpty?: IfEmptyProps;
  archiveViewSwitchState?: boolean;
  /** When set, column headers match `headerKey` on sort columns (service-catalog style). Caller should `useMemo` this for stable identity. */
  sortConfig?: TableSortConfig<string>;
  /** Extra className applied to the table's outer wrapper, for callers that need to scope table-specific CSS (e.g. column widths) without affecting other tables sharing this component. */
  wrapperClassName?: string;
}

export const ITEMS_PER_PAGE = TICKET_PAGINATION.ITEM_PER_PAGE();

export const TableWrapper = ({
  headers,
  rows = [],
  routeName,
  onRowClickHandler,
  headerNotTitleCase,
  setCurrentPage,
  hideSearch,
  searchText,
  onSearchKeyChanged,
  archiveViewSwitch,
  onArchiveSwitchChange,
  archiveViewSwitchState,
  sortConfig,
  wrapperClassName,
}: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [searchKey, setSearchKey] = useState(searchText ?? '');
  const [data, setData] = useState<RowsData[]>([]);
  const $tableWrapper = useRef<HTMLDivElement | null>(null);

  const searchedResult = (val: string) => {
    tableSearchedResult(setData, rows, val);
  }

  useEffect(() => {
    if (onSearchKeyChanged)
      onSearchKeyChanged(searchKey);
    if (hideSearch && searchText)
      searchedResult(searchText);
    else
      searchedResult(searchKey);
  }, [searchKey, searchText, rows]);

  const handleRowClick = useCallback(
    (id: number) => {
      if (onRowClickHandler) {
        onRowClickHandler(id);
      } else {
        let path = `/settings/${routeName}?id=${id}`;
        if (location.pathname.indexOf('/settings/') == -1) path = `/${routeName}?id=${id}`;
        history.push(path);
      }
    },
    [onRowClickHandler, routeName, location.pathname, history]
  );

  const sortPersistOptions = useMemo(() => {
    if (!sortConfig) return null;
    const firstColumnField = sortConfig.columns[0]?.field;
    const fallbackDefaultSort =
      firstColumnField != null
        ? { sortField: firstColumnField, sortDirection: "asc" as const }
        : sortConfig.defaultSort;
    return {
      storageKey: sortConfig.storageKey,
      defaultSort: fallbackDefaultSort,
      validFields: sortConfig.columns.map((c) => c.field),
      compare: buildRowsDataCompare(sortConfig.columns),
    };
  }, [sortConfig]);

  const { sortField, sortDirection, handleSortColumnClick, sortedItems } =
    usePersistedTableSort(data, sortPersistOptions);

  const displayData = sortConfig ? sortedItems : data;

  const tableHeader = useMemo((): HeaderList => {
    if (sortConfig) {
      return buildSortableTableHeader(
        headers,
        !!headerNotTitleCase,
        sortConfig,
        sortField,
        sortDirection,
        handleSortColumnClick
      );
    }
    return getHeaders(headers, !!headerNotTitleCase);
  }, [
    sortConfig,
    headers,
    headerNotTitleCase,
    sortField,
    sortDirection,
    handleSortColumnClick,
  ]);

  const getRows = useMemo(() => (): RowListObject[] => {
    return tableGetRows(displayData, headers, handleRowClick);
  }, [displayData, headers, handleRowClick]);

  const onSearch = (e: React.SyntheticEvent<HTMLElement>) => {
    tableOnSearch(setSearchKey, searchedResult, setCurrentPage, e);
  }

  const handleArchiveSwitchChange = (isArchived: boolean) => {
    if (onArchiveSwitchChange) {
      onArchiveSwitchChange(isArchived);
    }
  }

  return (
    <div className='relative'>
      <div className='p-2'>
        <Flex hAlign='end' gap='gap.medium'>
          {archiveViewSwitch && (
            <Flex.Item>
              <Checkbox label={t('preferences.show-archived')} toggle checked={archiveViewSwitchState} onChange={() => handleArchiveSwitchChange(!archiveViewSwitchState)} />
            </Flex.Item>
          )}

          {!hideSearch && (
            <Flex.Item>
              <Flex style={{ justifyContent: 'flex-end' }}>
                <Input
                  icon={<SearchIcon />}
                  inverted
                  placeholder={t('common.search')}
                  value={searchKey}
                  onChange={onSearch}
                />
              </Flex>
            </Flex.Item>
          )}
        </Flex>
      </div>
      <div className='overflow-auto' style={{ height: "calc(100vh - 155px - 2.25rem)"}}>
        <FluentUIThemeConsumer
          render={(globalTheme) => (
            <RenderThemedTable
              globalTheme={globalTheme}
              $tableWrapper={$tableWrapper}
              headers={headers}
              headerNotTitleCase={headerNotTitleCase}
              getRows={getRows}
              headerOverride={sortConfig ? tableHeader : undefined}
              wrapperClassName={wrapperClassName}
            />
          )}
        />
      </div>
      <div className="h-9 flex items-center"><ResultCount count={getRows().length} /></div>
    </div>
  );
};

export const tableSearchedResult = (setData: any, rows: RowsData[], val: string) => {
  const query: string = val.toLowerCase();

  const filteredData: RowsData[] = rows.filter((row: RowsData) =>
    Object.keys(row).some(
      (key: string) =>
        typeof row[key] !== 'object' && // this check filters out the JSX elements
        (row[key]?.toString().toLowerCase().includes(query) ?? false)
    )
  );
  setData(filteredData);
};

export const tableMoveToPage = (setPaginatedData: any, setCurrentPage: any, collection: RowsData[], currentPage: number, type: string) => {
  if (type === 'next') {
    const newData: RowsData[] = collection.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);
    setCurrentPage((prevPage) => prevPage + 1);
    setPaginatedData(newData);
  } else {
    const newData: RowsData[] = collection.slice(
      (currentPage - 1) * ITEMS_PER_PAGE - ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE
    );
    setCurrentPage((prevPage) => prevPage - 1);
    setPaginatedData(newData);
  }
};

export const tableOnSearch = (setSearchKey: any, searchedResult: any, setCurrentPage: any, e: React.SyntheticEvent<HTMLElement>) => {
  const value = (e.target as HTMLInputElement).value
  setSearchKey(value);
  searchedResult(value);
  setCurrentPage(() => 1)
}

export const tableGetRows = (paginatedData: RowsData[], headers: string[], handleRowClick: any, className?: string) => {
  const result: RowListObject[] = [];

  paginatedData.forEach((row: RowsData, rowIndex: number) => {
    const rowListObject: RowListObject = {
      key: rowIndex,
      items: [],
      onClick: () => headers != USER_LICENSE_HEADERS ? handleRowClick(row['id'] as number) : {},
      className: className ?? `${Styles.tableRow}`,
    };

    const items: RowItemsObject[] = [];
    headers.forEach((header: string, headerIndex: number) => {
      const rowItemsObject: RowItemsObject = { content: '', key: '' };

      rowItemsObject['content'] =
        items.length === 0 ? (
          <div className={Styles.rowFirstColumn}>{row[header]}</div>
        ) : (
          row[header] ?? ''
        );

      rowItemsObject['key'] = `cell-${headerIndex}`;
      rowItemsObject['styles'] = { alignSelf: 'center' };

      items.push(rowItemsObject);
    });
    rowListObject.items = [...items];
    result.push(rowListObject);
  });

  return result;
}

interface ThemedTableProps {
  globalTheme: any,
  $tableWrapper: any,
  headers: string[],
  headerNotTitleCase: boolean,
  getRows: any,
  headerOverride?: HeaderList,
  wrapperClassName?: string,
}

export const RenderThemedTable = ({ globalTheme, $tableWrapper, headers, headerNotTitleCase, getRows, headerOverride, wrapperClassName }: ThemedTableProps) => {
  return <>
    <KBTableTheme globalTheme={globalTheme}>
      <div
        ref={$tableWrapper}
        className={wrapperClassName}
        style={{
          width: '100%',
          height: 'inherit',
          backgroundColor:
            globalTheme.siteVariables.colorScheme.default.background2,
          paddingBottom: '1rem',
          ...ThemeColorScheme(globalTheme.siteVariables),
        }}
      >
        <Table
          compact
          {...headers.length > 0 && {
            header:
              headerOverride ?? getHeaders(headers, !!headerNotTitleCase),
          }}
          rows={getRows()}
          aria-label='Nested navigation'
          accessibility={gridNestedBehavior}
          className={`responsive-table`}
          variables={({
            theme,
            colorScheme,
          }: SiteVariablesPrepared) => {
            return {
              rowBorderColor: colorScheme.default.backgroundActive,
              // box model
              compactRowHeight: '2.5rem',
              defaultRowMinHeight: '3rem',
              defaultRowVerticalPadding: '.8125rem',
              ...{
                defaultRowHeight: 'auto',
                cellVerticalAlignment: 'flex-start',
              },
              // colors
              backgroundColor:
                theme === TeamsTheme.HighContrast
                  ? colorScheme.grey.background2
                  : colorScheme.grey.background,
              ...(theme === TeamsTheme.HighContrast
                ? {
                  rowBorderColor: colorScheme.grey.foreground,
                  rowBorderHoverColor: colorScheme.grey.foreground,
                }
                : {}),
            };
          }}
          styles={{
            width: 'auto',
          }}
        />
      </div>
    </KBTableTheme>
  </>
}

interface MoreProps {
  t: any,
  moreEditAction?: () => void
}
export const MoreTableActions = ({ t, moreEditAction }: MoreProps) => {

  return (
    <TableAction items={[
      {
        content: t('teams.teams-columns.edit'),
        icon: <FontIcon iconName={"Edit"} className={`text-lg`} style={{ lineHeight: "unset" }} />,
        onClick: (e) => {
          e.stopPropagation();
          moreEditAction && moreEditAction();
        }
      }
    ]} />
  );
}
